import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía de onboarding inicial de una nueva alcaldía en MiPueblo.
 * Pensada para que el super_admin (equipo MiPueblo) la siga la primera
 * vez que se da de alta un cliente. Cubre desde crear el tenant hasta
 * que el primer ciudadano puede radicar un PQRSDF.
 */
export function DocsOnboardingPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Onboarding de alcaldía
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Onboarding inicial de una alcaldía
        </h1>

        <div className="callout">
          <strong>Tiempo total:</strong> 30-60 min activos. Cubre desde crear
          el tenant hasta que un ciudadano puede radicar su primer PQRSDF.
        </div>

        {/* ---- Datos previos ---- */}
        <Section title="Datos que necesitas de la alcaldía">
          <Checklist
            items={[
              <>
                <strong>Nombre legal exacto</strong> (como aparece en el RUT) —
                ej: <code>Alcaldía Municipal de Cajicá</code>
              </>,
              <>
                <strong>NIT con dígito de verificación</strong> — ej:{" "}
                <code>899999114-1</code>
              </>,
              <>
                <strong>Departamento + Municipio</strong> + código DIVIPOLA si
                lo tienes (lo sugerimos solos al elegir municipio)
              </>,
              <>
                <strong>Email institucional del contacto principal</strong> —
                ej: <code>tic@cajica.gov.co</code>. No usar gmail/hotmail.
              </>,
              <>
                <strong>Teléfono</strong> del conmutador (opcional pero
                recomendado)
              </>,
              <>
                <strong>Logo</strong> en SVG o PNG transparente (al menos
                512x512) si tienen branding propio
              </>,
              <>
                <strong>Datos del alcalde</strong> y la persona designada como{" "}
                <strong>admin_tenant</strong> (rol más alto dentro de la
                alcaldía)
              </>,
            ]}
          />
        </Section>

        {/* ---- Paso 1 ---- */}
        <Section title="Paso 1 — Crear el tenant">
          <Ol>
            <li>
              Ir a <Kbd>Alcaldías</Kbd> en el sidebar → click{" "}
              <Kbd>Nueva alcaldía</Kbd>
            </li>
            <li>
              Llenar el formulario:
              <ul>
                <li>
                  <strong>Nombre</strong>: el legal exacto del RUT
                </li>
                <li>
                  <strong>Slug</strong>: se sugiere automáticamente desde el
                  nombre — debe ser único, sin tildes, en minúsculas. Es el
                  subdominio: <code>{`{slug}.mipuebloapp.com`}</code>. Una vez
                  creado <strong>NO se puede cambiar</strong> sin migrar datos.
                </li>
                <li>
                  <strong>NIT</strong>: con dígito de verificación
                </li>
                <li>
                  <strong>Departamento → Municipio</strong>: dropdowns
                  pre-cargados con DIVIPOLA oficial
                </li>
                <li>
                  <strong>Email contacto</strong> + <strong>teléfono</strong>{" "}
                  institucionales
                </li>
              </ul>
            </li>
            <li>
              Click <Kbd>Crear</Kbd>. El tenant aparece en estado{" "}
              <code>trial</code>. Su subdominio queda disponible en{" "}
              <code>{`https://{slug}.mipuebloapp.com`}</code> pero todavía no
              tiene usuarios.
            </li>
          </Ol>
          <div className="callout warn">
            <strong>Importante:</strong> el slug viaja en URLs, webhooks, y en
            el snippet del widget de chat. Elegir cuidado.
          </div>
        </Section>

        {/* ---- Paso 2 ---- */}
        <Section title="Paso 2 — Crear el primer admin del tenant">
          <p>
            El <code>admin_tenant</code> es quien va a administrar funcionarios,
            dependencias y configuración del lado de la alcaldía.
          </p>
          <Ol>
            <li>
              Entrar al tenant recién creado → tarjeta{" "}
              <Kbd>Funcionarios</Kbd>
            </li>
            <li>
              Click <Kbd>Nuevo funcionario</Kbd>:
              <ul>
                <li>
                  <strong>Nombre completo</strong>
                </li>
                <li>
                  <strong>Email</strong> (institucional, será su login)
                </li>
                <li>
                  <strong>Rol</strong>: <code>admin_tenant</code>
                </li>
                <li>
                  Dejar <strong>puede_chat</strong> en off (el admin no debería
                  estar atendiendo chats; eso es de operadores)
                </li>
              </ul>
            </li>
            <li>
              Click <Kbd>Crear</Kbd>. El sistema genera una{" "}
              <strong>contraseña temporal aleatoria</strong> y la muestra UNA
              SOLA VEZ.
            </li>
            <li>
              <strong>Copia la contraseña</strong> y envíasela al admin por un
              canal seguro (email institucional + teléfono para confirmar
              recepción). Al primer login el sistema le obligará a cambiarla.
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 3 ---- */}
        <Section title="Paso 3 — Configurar branding (logo + colores)">
          <p>
            Opcional pero muy recomendado para que el portal ciudadano se sienta
            de la alcaldía y no genérico.
          </p>
          <Ol>
            <li>
              Tenant → tarjeta <Kbd>Branding</Kbd>
            </li>
            <li>
              Subir <strong>logo</strong> (PNG transparente ≥512x512 o SVG)
            </li>
            <li>
              Definir <strong>color primario</strong> con el color picker — el
              valor por defecto es el dorado de MiPueblo
            </li>
            <li>
              <Kbd>Guardar</Kbd>. El portal ciudadano de{" "}
              <code>{`{slug}.mipuebloapp.com`}</code> ya muestra el branding,
              también el widget de chat.
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 4 ---- */}
        <Section title="Paso 4 — Configurar horarios de atención">
          <Ol>
            <li>
              Entrar al tenant como super_admin o pedirle al admin_tenant que
              entre a <Kbd>Horarios</Kbd> en su panel
            </li>
            <li>
              Por defecto se asume lun-vie 8:00-12:00 / 14:00-17:30. Ajustar
              según la jornada real (incluyendo viernes corto si aplica).
            </li>
            <li>
              Personalizar el <strong>mensaje fuera de horario</strong> con la
              info propia de la alcaldía (líneas de emergencia, etc.)
            </li>
            <li>
              Festivos de Colombia están pre-cargados y el sistema los respeta
              automáticamente — no hay que tocarlos.
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 5 ---- */}
        <Section title="Paso 5 — Crear dependencias + plantillas iniciales">
          <p>
            El tenant arranca con <strong>plantillas de respuesta por defecto</strong>{" "}
            (acuse, pedir info, trasladar, resuelto) ya sembradas. Solo necesitas
            adaptarlas al lenguaje de la alcaldía si quieren.
          </p>
          <Ol>
            <li>
              <Kbd>Dependencias</Kbd> (en el panel del admin_tenant): crear las
              áreas reales de la alcaldía (Secretaría de Gobierno, Salud,
              Hacienda, Movilidad, etc.). Cada una puede tener su propio
              horario y SLA.
            </li>
            <li>
              <Kbd>Plantillas de respuesta</Kbd>: revisar las default y
              ajustarlas — o dejarlas si están bien.
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 6 ---- */}
        <Section title="Paso 6 — Sembrar el Centro de ayuda (KB)">
          <p>
            El centro de ayuda hace 3 cosas:
          </p>
          <ul>
            <li>Resuelve consultas comunes 24/7 sin necesidad de operador</li>
            <li>
              Alimenta al bot RAG (si lo activas) para responder en lenguaje
              natural
            </li>
            <li>Está accesible en <code>{`{slug}.mipuebloapp.com/ayuda`}</code></li>
          </ul>
          <Ol>
            <li>
              <Kbd>Centro de ayuda</Kbd> → <Kbd>Nuevo artículo</Kbd>
            </li>
            <li>
              Empezar con los <strong>10-15 trámites más comunes</strong> del
              municipio: pago de predial, certificado de residencia, horarios
              de oficinas, recolección de basuras, comparendos, etc.
            </li>
            <li>
              Cada artículo: <strong>título</strong> claro, <strong>resumen</strong>{" "}
              de 1-2 líneas, <strong>contenido</strong> completo en lenguaje
              ciudadano, <strong>categoría</strong>, marcar como{" "}
              <strong>publicado</strong>.
            </li>
          </Ol>
          <div className="callout tip">
            💡 Si la alcaldía ya tiene un sitio web con info de trámites, puedes
            copy-paste de ahí para acelerar. La calidad del KB determina cuánto
            se resuelve sin operador.
          </div>
        </Section>

        {/* ---- Paso 7 ---- */}
        <Section title="Paso 7 — Activar el bot RAG (opcional)">
          <p>
            Si el tenant tiene aprobado el uso de IA y la plataforma tiene al
            menos un proveedor configurado:
          </p>
          <Ol>
            <li>
              Tenant → tarjeta <Kbd>Bot del chat</Kbd>
            </li>
            <li>Activar el toggle</li>
            <li>
              Elegir proveedor (Anthropic Claude, OpenAI, Gemini, LM Studio,
              Ollama) + modelo. Si dudas, usa <code>claude-haiku-4-5</code> (más
              económico).
            </li>
            <li>
              Usar el botón <Kbd>Probar el bot</Kbd> al final de la página con
              consultas reales del municipio para validar.
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 8 ---- */}
        <Section title="Paso 8 — Configurar canales digitales">
          <p>Tres canales típicos para alcaldía:</p>
          <Table
            head={["Canal", "Cómo se configura", "Notas"]}
            rows={[
              [
                "Chat web",
                "Tenant → Canales → Chat web",
                "Genera snippet para incrustar en sitio oficial de la alcaldía. También funciona nativo en su subdominio.",
              ],
              [
                "WhatsApp",
                "Tenant → Canales → WhatsApp",
                <>
                  Requiere Meta Cloud API. Seguir la{" "}
                  <Link to="/docs/whatsapp">guía dedicada de WhatsApp</Link>.
                </>,
              ],
              [
                "Email institucional",
                "Tenant → Canales → Email",
                "Necesita credenciales SMTP/IMAP del correo de la alcaldía. Tu equipo TIC los provee.",
              ],
            ]}
          />
        </Section>

        {/* ---- Paso 9 ---- */}
        <Section title="Paso 9 — Capacitación al admin del tenant">
          <p>
            Una vez creado el primer admin, agenda una sesión de 30-45 min para
            cubrir:
          </p>
          <Ul
            items={[
              "Cómo crear más funcionarios y asignar puede_chat",
              "Diferencia entre roles: admin / supervisor / operador",
              "Cómo monitorea PQRSDFs en la bandeja y los plazos legales (Ley 1755/2015)",
              "Vista del chat ciudadano: cola, mis chats, activas, cerradas",
              "Métricas que va a poder ver",
              "Centro de ayuda — cómo agregar/editar artículos",
              "Encuesta de satisfacción — cómo lee resultados",
            ]}
          />
        </Section>

        {/* ---- Paso 10 ---- */}
        <Section title="Paso 10 — Cambiar a estado activo">
          <p>
            Cuando el contrato esté firmado y se haya cobrado:
          </p>
          <Ol>
            <li>
              Tenant → <Kbd>Editar</Kbd> → cambiar <strong>plan</strong> al
              contratado (básico / pro / enterprise)
            </li>
            <li>
              Tenant → menú de estado → <Kbd>Activar</Kbd> (de trial a active)
            </li>
            <li>
              Mandar email de bienvenida con: URL del panel{" "}
              <code>{`{slug}.mipuebloapp.com`}</code>, credenciales del admin,
              link a esta documentación, contacto de soporte de MiPueblo.
            </li>
          </Ol>
        </Section>

        {/* ---- Checklist final ---- */}
        <Section title="Checklist final antes de entregar">
          <Checklist
            items={[
              "Tenant creado con NIT y municipio correctos",
              "Primer admin_tenant creado y contraseña entregada",
              "Logo + color primario configurados",
              "Horarios reales de la alcaldía cargados",
              "Dependencias principales creadas",
              "Mínimo 10 artículos publicados en centro de ayuda",
              "Bot RAG activado y probado (si aplica)",
              "Al menos 1 canal digital activo (web obligatorio)",
              "Capacitación al admin completada",
              "Estado del tenant en active (no trial) si ya contrató",
              "Email de bienvenida enviado con accesos y soporte",
            ]}
          />
        </Section>

        {/* ---- Cierre ---- */}
        <Section title="Después del onboarding">
          <p>
            Durante los primeros 30 días monitorea desde{" "}
            <Kbd>Métricas e informes</Kbd>:
          </p>
          <ul>
            <li>
              <strong>Volumen de PQRSDF</strong>: si crece bien, el onboarding
              fue exitoso. Si está plano, posible falta de difusión.
            </li>
            <li>
              <strong>% resueltos por bot</strong>: si es bajo, el KB necesita
              más artículos o el bot RAG está mal configurado.
            </li>
            <li>
              <strong>SLA cumplido</strong>: rojo = la alcaldía no está
              respondiendo a tiempo → re-capacitar al admin.
            </li>
            <li>
              <strong>NPS / encuestas</strong>: si las calificaciones son
              bajas, revisar las respuestas para entender por qué.
            </li>
          </ul>
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
